import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/b9pkkc6t_HERO%20VIDEO.mp4";
const CONTACT_URL = "https://www.weddingvideo.co.uk/contact";

const photos = [
  "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/lgsxk5pq_Bonnie__Josh_SP_.jpg",
  "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/2fbxfqwa_CHARLEY%20%26%20FABIO%20SP%2015-08-25%20%20040.jpg",
  "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/1qkcz52g_EMMA__MICHAEL_101023___471.jpg",
  "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/49pnxgu0_JESS%20%26%20PETE%2022-05-25%20%20384.jpg",
  "https://customer-assets.emergentagent.com/job_8d44df25-0f67-4d0d-b916-c24ae1fa8d16/artifacts/7x81ib5v_Oyin%20%26%20David%20Preview-24.jpg",
  "https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/4tj3d52l_Helen%20%26%20Daniel%2014-07-25%20%20009.jpg",
];

function CTA({ text = "Check My Date", large = false, testId = "cta" }) {
  return (
    <a
      href={CONTACT_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`group inline-flex items-center justify-center gap-2 bg-[#C2A878] text-[#0A0908] font-sans font-semibold tracking-wide uppercase hover:bg-[#d4bc92] transition-all duration-300 ${
        large ? "px-12 py-5 text-sm" : "px-8 py-4 text-xs"
      }`}
    >
      {text}
      <ArrowRight
        size={large ? 16 : 14}
        className="group-hover:translate-x-1 transition-transform"
      />
    </a>
  );
}

function DarkSection({ children, id, testId }) {
  return (
    <section
      id={id}
      data-testid={testId}
      className="bg-[#0A0908] py-24 md:py-32"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">{children}</div>
    </section>
  );
}

function LightSection({ children, id, testId }) {
  return (
    <section
      id={id}
      data-testid={testId}
      className="bg-[#FAF9F7] py-24 md:py-32"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-12">{children}</div>
    </section>
  );
}

function SectionHeading({ children, light = false }) {
  return (
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className={`font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light leading-tight mb-8 ${
        light ? "text-[#F5F5F0]" : "text-[#1A1917]"
      }`}
    >
      {children}
    </motion.h2>
  );
}

function Prose({ children, light = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className={`space-y-5 font-sans text-sm md:text-base leading-[1.85] ${
        light ? "text-[#A19D94]" : "text-[#5A5750]"
      }`}
    >
      {children}
    </motion.div>
  );
}

function Quote({ text, couple }) {
  return (
    <motion.blockquote
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border-l-2 border-[#C2A878] pl-6 my-8"
    >
      <p className="font-serif text-lg md:text-xl italic text-[#1A1917]/80 leading-relaxed">
        "{text}"
      </p>
      {couple && (
        <cite className="block mt-2 text-xs text-[#A19D94] font-sans not-italic">
          — {couple}
        </cite>
      )}
    </motion.blockquote>
  );
}

function QuoteDark({ text, couple }) {
  return (
    <motion.blockquote
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border-l-2 border-[#C2A878] pl-6 my-8"
    >
      <p className="font-serif text-lg md:text-xl italic text-[#F5F5F0]/80 leading-relaxed">
        "{text}"
      </p>
      {couple && (
        <cite className="block mt-2 text-xs text-[#A19D94]/60 font-sans not-italic">
          — {couple}
        </cite>
      )}
    </motion.blockquote>
  );
}

export default function WhyUsPage() {
  return (
    <div data-testid="why-us-page" className="bg-[#0A0908] min-h-screen">
      <Helmet>
        <title>
          Be At Your Wedding. We'll Capture It. | Wedding Video Essex
        </title>
        <meta
          name="description"
          content="We take normal people who swear they're awkward on camera and make them look incredible. Award-winning wedding videographers and photographers in Essex. 900+ weddings filmed."
        />
        <link
          rel="canonical"
          href="https://essex.weddingvideo.co.uk/why-us"
        />
      </Helmet>

      {/* ═══════ HERO ═══════ */}
      <section
        data-testid="why-hero"
        className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <h1
              data-testid="why-hero-heading"
              className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight font-light text-white leading-[1.1]"
            >
              Be at your wedding.
              <br />
              We'll capture it.
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="mt-8 text-sm md:text-base text-[#A19D94] font-sans max-w-xl mx-auto leading-relaxed"
            >
              Why couples choose Wedding Video Essex
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ NOT A PHOTOSHOOT ═══════ */}
      <LightSection testId="why-not-photoshoot">
        <SectionHeading>Your wedding day is not a photoshoot.</SectionHeading>
        <Prose>
          <p>
            This is your wedding day. Not a photoshoot. Not a film set.
          </p>
          <p>
            You hate posing. You hate the idea of being in front of a camera.
            You don't want to spend two hours of your wedding learning stiff
            poses while your guests sip drinks without you.
          </p>
          <p>
            You want to be present. Sipping cocktails during the drinks
            reception. Eating the incredible food during the meal. Dancing the
            night away with the people you love most.
          </p>
          <p>
            Photography and videography should capture all those natural,
            authentic and candid moments exactly as they happen. That's what we
            do.
          </p>
          <p className="text-[#1A1917] font-medium">
            We take normal people who swear they're awkward on camera and make
            them look incredible.
          </p>
        </Prose>
      </LightSection>

      {/* ═══════ PHOTO STRIP ═══════ */}
      <section className="bg-[#0A0908] py-4 overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="flex gap-2 md:gap-3"
        >
          {photos.map((src, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-[200px] md:w-[280px] h-[130px] md:h-[180px] overflow-hidden"
            >
              <img
                src={src}
                alt="Wedding photography Essex"
                className="w-full h-full object-cover opacity-70 hover:opacity-100 transition-opacity duration-500"
              />
            </div>
          ))}
        </motion.div>
      </section>

      {/* ═══════ BEFORE YOUR DAY ═══════ */}
      <DarkSection testId="why-before-day">
        <SectionHeading light>
          It starts before your wedding day.
        </SectionHeading>
        <Prose light>
          <p>
            The relaxed feeling doesn't start at 8am on the morning of your
            wedding. It starts on our first call.
          </p>
          <p>
            We want to get to know you as a couple. What you're into. What makes
            you laugh. What your day is actually going to feel like. That way, by
            the time we arrive, we're not strangers with cameras.
          </p>
        </Prose>
        <QuoteDark
          text="From our very first meeting with Alex, he instantly put us at ease."
        />
      </DarkSection>

      {/* ═══════ ON THE DAY ═══════ */}
      <LightSection testId="why-on-the-day">
        <SectionHeading>On the day, we're guests.</SectionHeading>
        <Prose>
          <p>
            We blend into the background. But not the awkward silent kind of
            guest. We're the organised, responsible friend who knows the agenda
            and keeps everyone gently in check. The one who rounds up your nan
            for the group shot and walks your grandparents back to their room.
          </p>
        </Prose>
        <Quote text="Alex and Ellis were like friends of the family." />
        <Quote text="It's like having a best man, a bridesmaid, a wedding coordinator and a friend." />
        <Prose>
          <p>
            Ask your guests afterwards. They'll tell you the same.
          </p>
          <p>
            That's the goal. When we do need to guide you, it's invisible
            direction. A word here, a nudge there. Never a command, never a stiff
            pose, never a camera in your face.
          </p>
        </Prose>
      </LightSection>

      {/* ═══════ MOMENTS YOU MISSED ═══════ */}
      <DarkSection testId="why-moments-missed">
        <SectionHeading light>
          The moments you never even saw.
        </SectionHeading>
        <Prose light>
          <p>
            Here's the thing nobody tells you about your wedding day. You miss
            half of it. Ask any married couple. It flies by in a blur.
          </p>
          <p>
            You're pulled in a hundred directions. Your dad wiping a tear during
            the ceremony. Your mates crying with laughter at the speeches. Your
            gran on the dance floor. You physically cannot be everywhere.
          </p>
          <p>We can.</p>
        </Prose>
        <QuoteDark text="There was so much detail that the guys captured that we never even noticed!!" />
        <Prose light>
          <p>
            Your film isn't just a memory of what you saw. It's your whole
            wedding, including everything you missed.
          </p>
        </Prose>
      </DarkSection>

      {/* ═══════ PHOTOGRAPHY ═══════ */}
      <LightSection testId="why-photography">
        <SectionHeading>
          Photography that ends up on the wall.
        </SectionHeading>
        <Prose>
          <p>
            We want to capture the mantelpiece shots. The ones of you two that
            your parents and grandparents will hang on the wall, beaming with
            pride.
          </p>
          <p>
            We'll also get the formal group shots with your immediate families
            and best friends. But we do it fast, we do it relaxed, and we let
            the natural flow of the day make it happen. No hour-long lineup. No
            cheesy prompts. You'll be back with your guests before your drink
            goes warm.
          </p>
        </Prose>
      </LightSection>

      {/* ═══════ THE FILM ═══════ */}
      <DarkSection testId="why-the-film">
        <SectionHeading light>The film is why we exist.</SectionHeading>
        <Prose light>
          <p>
            The storytelling in the film is why I started this back in 2015.
            Over 900 weddings later, I still get goosebumps in the speeches.
            The nervous tremors in the voices. The smiles, the laughter, the
            tears. Voices and reactions captured forever.
          </p>
          <p className="text-[#F5F5F0] font-medium">
            This isn't a wedding video. It's a film telling the story of the
            best day of your life.
          </p>
          <p>
            There are no retakes. No second chances. One day, captured once,
            kept forever. We treat that like the privilege it is.
          </p>
          <p>
            We weave the film together with the real audio from your day. The
            stories from loved ones during the speeches. Your partner saying how
            incredible you look. Your dad saying how proud he is. The vows,
            promises and commitments you make to each other during the ceremony,
            captured crisp and clear on hidden mics you'll forget are even there.
          </p>
          <p>
            And the music. Not some stock track you've never heard. The actual
            songs you chose. The one you walked down the aisle to. The one you
            had your first dance to. We build your film around them, so every
            time you press play you're right back there.
          </p>
        </Prose>
      </DarkSection>

      {/* ═══════ 72 HOURS ═══════ */}
      <LightSection testId="why-72-hours">
        <SectionHeading>Relive it within 72 hours.</SectionHeading>
        <Prose>
          <p>
            Three days after your wedding, when the wedding blues hit and it all
            feels like a dream, your sneak peek lands in your inbox. Film and
            photos, both within 72 hours. While your mates are still texting you
            about the day, you're already reliving it.
          </p>
        </Prose>
      </LightSection>

      {/* ═══════ GRANDKIDS ═══════ */}
      <DarkSection testId="why-grandkids">
        <SectionHeading light>
          A film your grandkids will watch.
        </SectionHeading>
        <Prose light>
          <p>
            A few months later, as the memories start to blur, the full delivery
            lands. A beautifully crafted cinematic film and your complete photo
            gallery. Every second of the ceremony. Every word of the speeches.
            The best day of your life, told as a story you'll love watching for
            years to come.
          </p>
        </Prose>
        <QuoteDark text="Watching the video felt like reliving the entire day again." />
        <Prose light>
          <p>
            But here's the part nobody thinks about when they book. This becomes
            a family heirloom. Your kids watching it back in 20 years with their
            partners. Your grandkids watching it in 40. Long after we're all
            gone, your story will still be playing.
          </p>
          <p className="text-[#F5F5F0] font-medium">
            That's not a wedding video. That's a chapter of your family's
            history, captured forever.
          </p>
        </Prose>
      </DarkSection>

      {/* ═══════ TRUST CLOSE ═══════ */}
      <section
        data-testid="why-trust-close"
        className="bg-[#FAF9F7] py-24 md:py-36"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <SectionHeading>
            Would you trust all this with just anyone?
          </SectionHeading>
          <Prose>
            <p>
              Your memories of the biggest day of your life. One shot at
              capturing them. Gone forever if it's missed.
            </p>
            <p>
              Would you hand that to someone random? A mate with a decent camera?
              The cheapest quote on Facebook?
            </p>
            <p>
              Or would you trust a team that's captured over 900 couples across
              Essex and told every single one of their stories. A team that's won
              seven wedding awards for their commitment and service to their
              couples. A team with over 400 five-star reviews. A team that knows
              you hate the camera, and guarantees to make you look and feel
              amazing anyway.
            </p>
            <p className="text-[#1A1917] font-medium text-base md:text-lg leading-relaxed">
              We're not just wedding photographers and videographers. We're your
              photo and video team. The friendly face on the morning. The
              organised guest on the day. The storytellers after it. And the
              reason your grandkids will know exactly how it felt to be there.
            </p>
          </Prose>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-14 text-center"
          >
            <CTA text="Check If Your Date Is Free" large testId="why-final-cta" />
            <p className="mt-3 text-xs text-[#A19D94]/50 font-sans">
              No obligation. We reply within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="bg-[#0A0908] border-t border-white/[0.04] py-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-serif text-lg text-[#F5F5F0]/80 tracking-tight">
            Wedding Video{" "}
            <span className="text-[#C2A878]">Essex</span>
          </p>
          <p className="text-xs text-[#A19D94]/40 font-sans mt-2">
            Award-winning wedding videography and photography. Wickford, Essex.
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <a
              href="https://www.instagram.com/weddingvideoessex"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#A19D94]/50 hover:text-[#C2A878] font-sans transition-colors"
            >
              Instagram
            </a>
            <a
              href="https://www.youtube.com/@WeddingVideoEssex"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#A19D94]/50 hover:text-[#C2A878] font-sans transition-colors"
            >
              YouTube
            </a>
            <a
              href="https://essex.weddingvideo.co.uk"
              className="text-xs text-[#A19D94]/50 hover:text-[#C2A878] font-sans transition-colors"
            >
              Main Site
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
